import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import Modal from '../components/Modal';
import { DataTable, ErrorBox, dash, money, type Column, type Row } from '../components/DataTable';
import { KIND_A, KIND_B, kindLabel } from '../lib/driverKind';
import { SalaryLinesView } from './SettleStatus';

/**
 * 정산 총괄(2026-10-28 way) — 구 버스인 「소속별 정산현황」·「기사정산」을 한 탭으로. 회계팀용 · **마감 전에도 실시간**.
 *
 * - 서버 `getSettleSummary`(settleSummary.ts) = 정산현황과 **같은 계산 문장**을 편성(차주·차량·기사) 단위로 묶은 줄.
 *   받는 사람별 합은 정산현황 차인지급액과 원 단위까지 같다(`settle-summary-check.mjs`).
 * - 위 표 = 소속(지급 법인 — 기사 설정이 없으면 편성 소속) × 기사구분. 칸 = 인원 · 차인지급액 · 청구 대기. 칸을 누르면 아래 목록이 그 칸만.
 * - 🔴 「청구 대기」 = 거래처 청구가 안 돼 아직 명세에 못 들어간 운행료·영업정산(청구되면 차인지급액으로 옮겨 간다).
 * - 아래 목록 = 「편성별」(레거시 소속별 정산현황) / 「받는 사람별」(레거시 기사정산 — 차주·관리기사A 한 표). 줄을 누르면 정산현황 줄 보기.
 * - 합은 금액 문자열을 1/10000 원 정수(BigInt)로 더한다 — Number 로 더하면 소수 4자리가 깨진다.
 */

type Cell = { payees: Set<string>; grand: bigint; held: bigint };
const B = (v: unknown): bigint => {
  const s = String(v ?? '0'); const neg = s.startsWith('-'); const [i, f = ''] = (neg ? s.slice(1) : s).split('.');
  const x = BigInt(i || '0') * 10000n + BigInt((f + '0000').slice(0, 4)); return neg ? -x : x;
};
const S = (x: bigint): string => {
  const neg = x < 0n; const a = neg ? -x : x; const r = (a + 5000n) / 10000n; return `${neg && r !== 0n ? '-' : ''}${r}`;
};
const KINDS = ['관리기사A', '관리기사B', '직영사업장', '그 외'] as const;
const kindCol = (k: unknown): (typeof KINDS)[number] =>
  k === KIND_A ? '관리기사A' : k === KIND_B ? '관리기사B' : String(k ?? '').includes('사업장') ? '직영사업장' : '그 외';
const NO_GRP = '(소속 없음)';
const grpOf = (r: Row) => String(r.pay_grp_name ?? '') || NO_GRP;
const payeeKey = (r: Row) => `${String(r.axis)}:${String(r.payee_id)}`;

const SUM_KEYS = ['drive_cost', 'req_plus', 'req_minus', 'cancel_plus', 'cancel_minus', 'sales_total',
  'mo_plus', 'mo_minus', 'daily_plus', 'daily_minus', 'fin_inst', 'fin_total', 'grand_total', 'held'] as const;
const M = (key: string, label: string): Column => ({ key, label, align: 'right', render: (r) => money(r[key]) });

export default function SettleSummary({ driveMon }: { driveMon: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ms, setMs] = useState<number | null>(null);
  const [pick, setPick] = useState<{ grp: string | null; kind: string | null }>({ grp: null, kind: null });
  const [view, setView] = useState<'alloc' | 'payee'>('alloc');
  const [onlyHeld, setOnlyHeld] = useState(false);
  const [onlyMinus, setOnlyMinus] = useState(false);
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState<Row | null>(null);

  useEffect(() => {
    if (!driveMon) return;
    let alive = true;
    setRows(null); setErr(null);
    api.getSettleSummary({ driveMon })
      .then((r) => { if (alive) { setRows(r.rows as Row[]); setMs(r.ms); } })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [driveMon]);

  // 위 표(소속 × 기사구분)
  const matrix = useMemo(() => {
    const m = new Map<string, Map<string, Cell>>();
    const add = (g: string, k: string, r: Row) => {
      const row = m.get(g) ?? new Map<string, Cell>(); m.set(g, row);
      const c = row.get(k) ?? { payees: new Set(), grand: 0n, held: 0n }; row.set(k, c);
      c.payees.add(payeeKey(r)); c.grand += B(r.grand_total); c.held += B(r.held);
    };
    for (const r of rows ?? []) {
      const g = grpOf(r); const k = kindCol(r.driver_kind);
      add(g, k, r); add(g, '합계', r); add('합계', k, r); add('합계', '합계', r);
    }
    const grps = [...m.keys()].filter((g) => g !== '합계').sort((a, b) => (a === NO_GRP ? 1 : b === NO_GRP ? -1 : a.localeCompare(b, 'ko')));
    return { m, grps: [...grps, '합계'] };
  }, [rows]);

  // 아래 목록 — 칸 · 검색 · 「청구 대기만」·「마이너스만」
  const filtered = useMemo(() => {
    const words = term.trim().toLowerCase().split(/[ ,]+/).filter(Boolean);
    return (rows ?? []).filter((r) =>
      (pick.grp === null || pick.grp === '합계' || grpOf(r) === pick.grp)
      && (pick.kind === null || pick.kind === '합계' || kindCol(r.driver_kind) === pick.kind)
      && (!words.length || words.some((w) => [r.owner_name, r.driver_name, r.car_no, r.pay_grp_name, r.alloc_grp_name]
        .some((v) => String(v ?? '').toLowerCase().includes(w)))));
  }, [rows, pick, term]);

  const listRows = useMemo(() => {
    let out: Row[];
    if (view === 'alloc') out = filtered;
    else {
      const by = new Map<string, Row & { _b: Record<string, bigint>; _cars: Set<string>; _grps: Set<string> }>();
      for (const r of filtered) {
        const k = payeeKey(r);
        let p = by.get(k);
        if (!p) {
          p = { ...r, name: r.axis === 'owner' ? r.owner_name : r.driver_name, _b: Object.fromEntries(SUM_KEYS.map((x) => [x, 0n])),
            _cars: new Set(), _grps: new Set() } as never;
          by.set(k, p!);
        }
        for (const x of SUM_KEYS) p!._b[x] = (p!._b[x] ?? 0n) + B(r[x]);
        if (r.car_no) p!._cars.add(String(r.car_no));
        p!._grps.add(grpOf(r));
      }
      out = [...by.values()].map((p) => ({
        ...p, ...Object.fromEntries(SUM_KEYS.map((x) => [x, S(p._b[x] ?? 0n)])),
        car_no: [...p._cars].join(', '), pay_grp_name: [...p._grps].join(', '),
      }));
    }
    return out.filter((r) => (!onlyHeld || B(r.held) !== 0n) && (!onlyMinus || B(r.payee_grand) < 0n));
  }, [filtered, view, onlyHeld, onlyMinus]);

  const total = useMemo(() => {
    const v: Record<string, unknown> = {};
    for (const x of SUM_KEYS) v[x] = S(listRows.reduce((a, r) => a + B(r[x]), 0n));
    return { count: listRows.length, values: v };
  }, [listRows]);

  const axisLabel = (r: Row) => (r.axis === 'owner' ? '차주' : '직영');
  const columns: Column[] = view === 'alloc' ? [
    { key: 'pay_grp_name', label: '소속(지급)', render: (r) => (
      <span title={r.pay_grp_differs ? `편성 소속 ${dash(r.alloc_grp_name)}` : undefined}>
        {dash(r.pay_grp_name)}{r.pay_grp_differs ? <span className="ml-1 text-xs text-ink-faint">(편성 {String(r.alloc_grp_name ?? '')})</span> : null}
      </span>) },
    { key: 'owner_name', label: '차주', phone: 'pin' },
    { key: 'car_no', label: '차량' },
    { key: 'driver_name', label: '기사' },
    { key: 'driver_kind', label: '기사구분', render: (r) => kindLabel(r.driver_kind) },
    { key: 'axis', label: '받는 사람', render: axisLabel, phone: 'hide' },
    M('drive_cost', '운행료'), M('req_plus', '영업 추가지급'), M('req_minus', '영업 공제'),
    M('cancel_plus', '배차취소수당'), M('cancel_minus', '배차취소공제'), M('sales_total', '영업계'),
    M('mo_plus', '월정기 지급'), M('mo_minus', '월정기 공제'), M('daily_plus', '재무 추가지급'), M('daily_minus', '재무 공제'),
    M('fin_inst', '할부금'), M('fin_total', '재무계'), M('grand_total', '차인지급액'),
    { key: 'held', label: '청구 대기', align: 'right', render: (r) => <span className={B(r.held) !== 0n ? 'text-amber-700' : ''}>{money(r.held)}</span> },
  ] : [
    { key: 'name', label: '받는 사람', phone: 'pin' },
    { key: 'axis', label: '구분', render: axisLabel },
    { key: 'driver_kind', label: '기사구분', render: (r) => (r.axis === 'owner' ? '—' : kindLabel(r.driver_kind)) },
    { key: 'pay_grp_name', label: '소속(지급)' },
    { key: 'car_no', label: '차량', phone: 'hide' },
    M('sales_total', '영업계'), M('fin_total', '재무계'),
    { key: 'grand_total', label: '차인지급액', align: 'right', render: (r) => <span className={B(r.grand_total) < 0n ? 'text-red-600' : ''}>{money(r.grand_total)}</span> },
    { key: 'held', label: '청구 대기', align: 'right', render: (r) => <span className={B(r.held) !== 0n ? 'text-amber-700' : ''}>{money(r.held)}</span> },
    { key: 'is_salary', label: '지급발급', render: (r) => (r.is_salary ? '✓' : '') },
    { key: 'is_salary_open', label: '지급공개', render: (r) => (r.is_salary_open ? '✓' : '') },
  ];

  const cellBtn = (g: string, k: string) => {
    const c = matrix.m.get(g)?.get(k);
    const on = (pick.grp ?? '합계') === g && (pick.kind ?? '합계') === k;
    const strong = g === '합계' || k === '합계';
    return (
      <td key={k} className={`border border-line p-0 ${strong ? 'bg-canvas' : ''}`}>
        <button
          type="button"
          disabled={!c}
          onClick={() => setPick({ grp: g, kind: k })}
          className={`block w-full px-2 py-1 text-right tabular-nums ${on ? 'bg-brand-50 ring-1 ring-brand-400' : 'hover:bg-brand-50/50'} ${c ? '' : 'text-ink-faint'}`}
        >
          {c ? (
            <>
              <div className={strong ? 'font-semibold' : ''}>{money(S(c.grand))}</div>
              <div className="text-[11px] text-ink-soft">{c.payees.size}명{c.held !== 0n && <span className="ml-1 text-amber-700">대기 {money(S(c.held))}</span>}</div>
            </>
          ) : '—'}
        </button>
      </td>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
        <span title="명세 생성과 같은 계산 · 마감 전에도 지금 원장 그대로 · 소속 = 기사 소속(지급 법인), 없으면 편성 소속">
          ⓘ 지금 원장 기준 실시간 · 소속 = 지급 법인{ms !== null ? ` · ${(ms / 1000).toFixed(1)}초` : ''}
        </span>
        <span className="text-amber-700" title="거래처 청구가 안 돼 아직 명세에 못 들어간 운행료·영업정산 — 청구되면 차인지급액으로 옮겨 갑니다">청구 대기 = 청구 전이라 빠진 운행료·영업정산</span>
      </div>
      {err && <ErrorBox message={err} />}
      {rows === null && !err && <p className="text-sm text-ink-faint">불러오는 중…</p>}
      {rows && (
        <div className="overflow-x-auto">
          <table className="min-w-[44rem] border-collapse text-sm">
            <thead>
              <tr className="text-xs text-ink-soft">
                <th className="border border-line bg-canvas px-2 py-1 text-left">소속(지급) / 기사구분</th>
                {[...KINDS, '합계'].map((k) => <th key={k} className="border border-line bg-canvas px-2 py-1">{k}</th>)}
              </tr>
            </thead>
            <tbody>
              {matrix.grps.map((g) => (
                <tr key={g}>
                  <th className={`border border-line px-2 py-1 text-left font-normal ${g === '합계' ? 'bg-canvas font-semibold' : ''}`}>{g}</th>
                  {[...KINDS, '합계'].map((k) => cellBtn(g, k))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex overflow-hidden rounded border border-line text-xs">
              {(['alloc', 'payee'] as const).map((v) => (
                <button key={v} type="button" onClick={() => setView(v)}
                  className={`px-2 py-1 ${view === v ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
                  {v === 'alloc' ? '편성별' : '받는 사람별'}
                </button>
              ))}
            </span>
            <span className="text-xs text-ink-soft">
              {(pick.grp ?? '합계') === '합계' && (pick.kind ?? '합계') === '합계' ? '전체' : `${pick.grp ?? '합계'} · ${pick.kind ?? '합계'}`}
              {(pick.grp !== null || pick.kind !== null) && (
                <button type="button" className="ml-1 text-brand-700 underline" onClick={() => setPick({ grp: null, kind: null })}>전체 보기</button>
              )}
            </span>
            <input className="field w-48" placeholder="차주 · 기사 · 차량 · 소속" value={term} onChange={(e) => setTerm(e.target.value)} />
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={onlyHeld} onChange={(e) => setOnlyHeld(e.target.checked)} />청구 대기만</label>
            <label className="flex items-center gap-1 text-xs" title="받는 사람 차인지급액(전체 합)이 마이너스"><input type="checkbox" checked={onlyMinus} onChange={(e) => setOnlyMinus(e.target.checked)} />마이너스만</label>
          </div>
          <DataTable
            columns={columns}
            rows={listRows}
            loading={false}
            total={total}
            totalAt="top"
            tableId={`settle.summary.${view}`}
            dragSum
            onRowClick={(r) => setOpen(r)}
            exportXlsx={{ name: `정산총괄_${view === 'alloc' ? '편성별' : '받는사람별'}_${driveMon}` }}
          />
        </>
      )}
      {open && (
        <Modal width="max-w-4xl">
          <SalaryLinesView
            driveMon={driveMon}
            axis={open.axis === 'owner' ? 'owner' : 'direct'}
            row={{ target_id: open.payee_id, name: open.axis === 'owner' ? open.owner_name : open.driver_name, grand_total: open.payee_grand }}
            onClose={() => setOpen(null)}
          />
        </Modal>
      )}
    </div>
  );
}
